import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getClipSchedule,
  getTimelineEndTime,
} from "../features/projectWork/previewTimeline.js";
import { getExportChannelGain } from "../utils/exportMixLevel";

// Reschedule the dialogue clips when the audio clock and the video disagree by
// more than this; the video stays the authoritative clock.
const DIALOGUE_DRIFT_SEC = 0.06;
// M&E plays from media elements, which seek slowly, so they are nudged with
// playback rate and only re-seeked on large drift.
const STEM_NUDGE_SEC = 0.04;
const STEM_SEEK_SEC = 0.3;
const STEM_SEEK_COOLDOWN_MS = 1500;
const SYNC_CHECK_MS = 250;
const MAX_CACHED_BUFFERS = 40;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const fetchAndDecode = async (audioContext, url, signal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch preview audio: ${response.status}`);
  }
  return audioContext.decodeAudioData(await response.arrayBuffer());
};

/**
 * Plays a dialogue page as one timeline over the video: every clip is decoded
 * up front into a single AudioContext and scheduled against the video's
 * current time, so clips start sample-accurately at their dialogue start with
 * no per-clip handoff. The M&E stems play alongside, slaved to the video.
 *
 * Returns `ready` (every clip decoded or failed), `endTime` (when the last
 * clip stops sounding) and `unlock`, which must run inside a user gesture
 * before the first play so the browser lets the AudioContext start.
 */
export const usePagePreviewTimeline = ({
  enabled,
  videoElement,
  clips,
  dialogueVolume = 1,
  stemUrls = [],
}) => {
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  const buffersRef = useRef(new Map());
  const failedKeysRef = useRef(new Set());
  const loadingKeysRef = useRef(new Set());
  const clipFetchesRef = useRef(new AbortController());
  const activeSourcesRef = useRef([]);
  const anchorRef = useRef(null);
  const stemElementsRef = useRef([]);
  const lastStemSeekRef = useRef(0);
  const clipsRef = useRef(clips);
  const enabledRef = useRef(enabled);
  const [decodeVersion, setDecodeVersion] = useState(0);

  clipsRef.current = clips;
  enabledRef.current = enabled;

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      const masterGain = audioContext.createGain();
      masterGain.connect(audioContext.destination);
      audioContextRef.current = audioContext;
      masterGainRef.current = masterGain;
    }
    return audioContextRef.current;
  }, []);

  const unlock = useCallback(() => {
    getAudioContext().resume?.().catch(() => {});
  }, [getAudioContext]);

  const stopDialogue = useCallback(() => {
    activeSourcesRef.current.forEach(({ source, gain }) => {
      try {
        source.stop();
      } catch {
        // Already stopped.
      }
      source.disconnect();
      gain.disconnect();
    });
    activeSourcesRef.current = [];
    anchorRef.current = null;
  }, []);

  const pauseStems = useCallback(() => {
    stemElementsRef.current.forEach((element) => element.pause());
  }, []);

  const syncStems = useCallback(
    (forceSeek) => {
      const video = videoElement;
      if (!video) return;

      const now = performance.now();
      stemElementsRef.current.forEach((element) => {
        if (!element.src) return;

        const drift = element.currentTime - video.currentTime;
        const canSeek =
          forceSeek || now - lastStemSeekRef.current > STEM_SEEK_COOLDOWN_MS;

        if (
          (forceSeek && Math.abs(drift) > STEM_NUDGE_SEC) ||
          (Math.abs(drift) > STEM_SEEK_SEC && canSeek && !element.seeking)
        ) {
          element.currentTime = video.currentTime;
          element.playbackRate = video.playbackRate;
          lastStemSeekRef.current = now;
        } else if (Math.abs(drift) > STEM_NUDGE_SEC) {
          element.playbackRate =
            video.playbackRate * (1 - clamp(drift * 0.5, -0.05, 0.05));
        } else {
          element.playbackRate = video.playbackRate;
        }

        if (element.paused) {
          element.play().catch(() => {});
        }
      });
    },
    [videoElement]
  );

  // Start every clip that is still to sound from the video's current time.
  const scheduleDialogue = useCallback(() => {
    stopDialogue();

    const video = videoElement;
    if (!video || !enabledRef.current) return;

    const audioContext = getAudioContext();
    const videoTime = video.currentTime;
    const rate = video.playbackRate || 1;
    const startAt = audioContext.currentTime;

    clipsRef.current.forEach((clip) => {
      const buffer = buffersRef.current.get(clip.cacheKey);
      const schedule = buffer
        ? getClipSchedule(clip.start, buffer.duration, videoTime)
        : null;
      if (!schedule) return;

      const source = audioContext.createBufferSource();
      const gain = audioContext.createGain();
      source.buffer = buffer;
      source.playbackRate.value = rate;
      gain.gain.value = clip.gain * getExportChannelGain(buffer);
      source.connect(gain);
      gain.connect(masterGainRef.current);
      source.start(startAt + schedule.delay / rate, schedule.offset);
      activeSourcesRef.current.push({ source, gain });
    });

    anchorRef.current = { contextTime: startAt, videoTime, rate };
  }, [getAudioContext, stopDialogue, videoElement]);

  const isVideoPlaying = useCallback(
    () =>
      Boolean(
        videoElement &&
          !videoElement.paused &&
          !videoElement.seeking &&
          videoElement.readyState >= 3
      ),
    [videoElement]
  );

  const startAll = useCallback(() => {
    if (!enabledRef.current) return;
    scheduleDialogue();
    syncStems(true);
  }, [scheduleDialogue, syncStems]);

  const stopAll = useCallback(() => {
    stopDialogue();
    pauseStems();
  }, [pauseStems, stopDialogue]);

  // Decode the page's clips ahead of playback.
  useEffect(() => {
    if (!enabled) return;

    const audioContext = getAudioContext();
    const pending = clips.filter(
      (clip) =>
        !buffersRef.current.has(clip.cacheKey) &&
        !failedKeysRef.current.has(clip.cacheKey) &&
        !loadingKeysRef.current.has(clip.cacheKey)
    );

    pending.forEach((clip) => {
      loadingKeysRef.current.add(clip.cacheKey);
      fetchAndDecode(audioContext, clip.url, clipFetchesRef.current.signal)
        .then((buffer) => {
          buffersRef.current.set(clip.cacheKey, buffer);
        })
        .catch((error) => {
          if (error?.name === "AbortError") return;
          console.error("Unable to load preview audio", {
            dialogueIndex: clip.index,
            error: error?.message,
          });
          failedKeysRef.current.add(clip.cacheKey);
        })
        .finally(() => {
          loadingKeysRef.current.delete(clip.cacheKey);
          setDecodeVersion((version) => version + 1);
        });
    });

    // Keep the cache bounded to roughly the current and recent pages.
    if (buffersRef.current.size > MAX_CACHED_BUFFERS) {
      const currentKeys = new Set(clips.map((clip) => clip.cacheKey));
      [...buffersRef.current.keys()].forEach((key) => {
        if (!currentKeys.has(key)) buffersRef.current.delete(key);
      });
    }
  }, [clips, enabled, getAudioContext]);

  const ready = useMemo(
    () =>
      clips.every(
        (clip) =>
          buffersRef.current.has(clip.cacheKey) ||
          failedKeysRef.current.has(clip.cacheKey)
      ),
    // decodeVersion re-evaluates readiness as decodes settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clips, decodeVersion]
  );

  const endTime = useMemo(
    () => getTimelineEndTime(clips, buffersRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clips, decodeVersion]
  );

  // Pick up edits (new audio, "Use original", volume) while playing.
  useEffect(() => {
    if (enabled && isVideoPlaying()) {
      scheduleDialogue();
    }
  }, [clips, decodeVersion, enabled, isVideoPlaying, scheduleDialogue]);

  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = Math.max(0, Number(dialogueVolume) || 0);
    }
  }, [dialogueVolume, decodeVersion, enabled]);

  // One media element per M&E stem; only the source changes on URL refresh.
  // Nothing downloads until the preview is first turned on.
  useEffect(() => {
    if (!enabled) return;

    const elements = stemElementsRef.current;
    stemUrls.forEach((url, index) => {
      if (!elements[index]) {
        const element = new Audio();
        element.preload = "auto";
        elements[index] = element;
      }
      if (elements[index].src !== url) {
        const resumeAt = elements[index].currentTime;
        elements[index].src = url;
        elements[index].currentTime = resumeAt;
      }
    });
    elements.splice(stemUrls.length).forEach((element) => {
      element.pause();
      element.removeAttribute("src");
      element.load();
    });

    if (enabledRef.current && isVideoPlaying()) {
      syncStems(true);
    }
  }, [enabled, isVideoPlaying, stemUrls, syncStems]);

  // Follow the video: start on playing, stop whenever it pauses or stalls.
  useEffect(() => {
    const video = videoElement;
    if (!video || !enabled) {
      stopAll();
      return undefined;
    }

    const handleResume = () => {
      if (isVideoPlaying()) startAll();
    };

    const events = {
      playing: handleResume,
      seeked: handleResume,
      ratechange: handleResume,
      pause: stopAll,
      waiting: stopAll,
      seeking: stopAll,
      ended: stopAll,
    };

    Object.entries(events).forEach(([name, handler]) =>
      video.addEventListener(name, handler)
    );

    handleResume();

    const driftTimer = window.setInterval(() => {
      if (!isVideoPlaying()) return;

      const anchor = anchorRef.current;
      const audioContext = audioContextRef.current;
      if (!anchor || !audioContext) {
        startAll();
        return;
      }

      const expectedVideoTime =
        anchor.videoTime +
        (audioContext.currentTime - anchor.contextTime) * anchor.rate;
      if (Math.abs(video.currentTime - expectedVideoTime) > DIALOGUE_DRIFT_SEC) {
        scheduleDialogue();
      }
      syncStems(false);
    }, SYNC_CHECK_MS);

    return () => {
      Object.entries(events).forEach(([name, handler]) =>
        video.removeEventListener(name, handler)
      );
      window.clearInterval(driftTimer);
      stopAll();
    };
  }, [
    enabled,
    isVideoPlaying,
    scheduleDialogue,
    startAll,
    stopAll,
    syncStems,
    videoElement,
  ]);

  useEffect(
    () => () => {
      clipFetchesRef.current.abort();
      clipFetchesRef.current = new AbortController();
      stemElementsRef.current.forEach((element) => {
        element.pause();
        element.removeAttribute("src");
      });
      stemElementsRef.current = [];
      audioContextRef.current?.close?.();
      audioContextRef.current = null;
    },
    []
  );

  return { ready, endTime, unlock };
};
